<template>
  <div class="app-container leader-jobs-page">
    <div class="page-heading">
      <div class="page-title">
        <el-button icon="el-icon-back" @click="backToHunter">返回尋找領隊</el-button>
        <div>
          <h2>徵才啟事</h2>
        </div>
      </div>
      <el-button
        type="primary"
        icon="el-icon-plus"
        :disabled="featureDisabled"
        @click="openEditor()"
      >
        新增徵才
      </el-button>
    </div>

    <el-alert
      v-if="featureDisabled"
      class="load-alert"
      type="warning"
      title="專區已關閉"
      description="領隊導遊專區目前已關閉，新增、狀態變更等寫操作已停用；如需開放，請聯絡系統管理員。"
      :closable="false"
      show-icon
    />

    <el-card shadow="never" class="filter-card">
      <el-form :inline="true" :model="query" @submit.native.prevent>
        <el-form-item label="關鍵字">
          <el-input
            v-model.trim="query.keyword"
            clearable
            maxlength="100"
            placeholder="標題、摘要"
            @keyup.enter.native="search"
          />
        </el-form-item>
        <el-form-item v-if="isContentAdmin" label="發布單位">
          <el-select v-model="query.agencyCode" clearable filterable placeholder="全部旅行社">
            <el-option
              v-for="agency in agencyOptions"
              :key="agency.key"
              :label="agency.val"
              :value="agency.key"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="身分">
          <el-select v-model="query.roleType" clearable placeholder="全部">
            <el-option label="領隊" value="Leader" />
            <el-option label="導遊" value="Guide" />
            <el-option label="皆可" value="Both" />
          </el-select>
        </el-form-item>
        <el-form-item label="分類">
          <el-input v-model.trim="query.category" clearable maxlength="100" />
        </el-form-item>
        <el-form-item label="目的地">
          <el-input v-model.trim="query.destination" clearable maxlength="200" />
        </el-form-item>
        <el-form-item label="工作開始">
          <el-date-picker
            v-model="query.startDate"
            clearable
            value-format="yyyy-MM-dd"
            placeholder="開始日"
          />
        </el-form-item>
        <el-form-item label="工作結束">
          <el-date-picker
            v-model="query.endDate"
            clearable
            value-format="yyyy-MM-dd"
            placeholder="結束日"
          />
        </el-form-item>
        <el-form-item label="狀態">
          <el-select v-model="query.status" clearable placeholder="全部">
            <el-option
              v-for="option in statusOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-select v-model="query.sort">
            <el-option label="最新建立" value="latest" />
            <el-option label="推薦優先" value="recommended" />
            <el-option label="截止日期" value="deadline" />
            <el-option label="工作日期" value="startDate" />
            <el-option label="薪資由高至低" value="salaryDesc" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="search">
            查詢
          </el-button>
          <el-button icon="el-icon-refresh" :disabled="loading" @click="reset">重設</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-alert
      v-if="loadError"
      class="load-alert"
      type="error"
      :title="loadError"
      :closable="false"
      show-icon
    />

    <el-table v-loading="loading" :data="jobs" border stripe empty-text="尚無徵才啟事">
      <el-table-column prop="title" label="標題" min-width="220" show-overflow-tooltip />
      <el-table-column v-if="isContentAdmin" prop="agencyName" label="發布單位" min-width="160" />
      <el-table-column label="身分" width="90">
        <template slot-scope="scope">{{ roleLabel(scope.row.roleType) }}</template>
      </el-table-column>
      <el-table-column prop="destination" label="目的地" min-width="120" show-overflow-tooltip />
      <el-table-column label="工作日期" min-width="190">
        <template slot-scope="scope">{{ dateRange(scope.row) }}</template>
      </el-table-column>
      <el-table-column label="薪資" min-width="130">
        <template slot-scope="scope">{{ compensationLabel(scope.row) }}</template>
      </el-table-column>
      <el-table-column label="應徵" width="90" align="center">
        <template slot-scope="scope">
          {{ scope.row.applicationCount || 0 }} / {{ scope.row.quota }}
        </template>
      </el-table-column>
      <el-table-column v-if="isContentAdmin" label="推薦" width="90" align="center">
        <template slot-scope="scope">
          <el-switch
            :value="scope.row.isRecommended"
            :disabled="isRowUpdating(scope.row.id) || scope.row.status === 'Closed' || featureDisabled"
            :aria-label="`${scope.row.title}推薦開關`"
            @change="updateRecommendation(scope.row, $event)"
          />
        </template>
      </el-table-column>
      <el-table-column v-if="isContentAdmin" label="推薦排序" width="130" align="center">
        <template slot-scope="scope">
          <el-input-number
            :value="scope.row.recommendSort"
            :min="0"
            :max="9999"
            size="mini"
            controls-position="right"
            :disabled="isRowUpdating(scope.row.id) || scope.row.status === 'Closed' || featureDisabled"
            :aria-label="`${scope.row.title}推薦排序`"
            @change="updateRecommendSort(scope.row, $event)"
          />
        </template>
      </el-table-column>
      <el-table-column label="狀態" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="statusTagType(scope.row.status)">{{
            jobStatusLabel(scope.row.status)
          }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="450" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" @click="openPreview(scope.row)">預覽</el-button>
          <el-button
            size="mini"
            :disabled="scope.row.status === 'Closed' || featureDisabled"
            @click="openEditor(scope.row)"
          >
            編輯
          </el-button>
          <el-button size="mini" :disabled="featureDisabled" @click="copyJob(scope.row)">
            複製
          </el-button>
          <el-button size="mini" type="info" @click="openApplications(scope.row)">
            應徵者
          </el-button>
          <el-button
            v-if="scope.row.status !== 'Published' && scope.row.status !== 'Closed'"
            size="mini"
            type="success"
            :disabled="featureDisabled"
            @click="changeStatus(scope.row, 'Published')"
          >
            發布
          </el-button>
          <el-button
            v-if="scope.row.status === 'Published'"
            size="mini"
            type="warning"
            :disabled="featureDisabled"
            @click="changeStatus(scope.row, 'Unpublished')"
          >
            下架
          </el-button>
          <el-button
            v-if="scope.row.status !== 'Closed'"
            size="mini"
            type="danger"
            plain
            :disabled="featureDisabled"
            @click="changeStatus(scope.row, 'Closed')"
          >
            結束
          </el-button>
          <el-button
            v-if="scope.row.status === 'Draft' || scope.row.status === 'Unpublished'"
            size="mini"
            type="danger"
            :disabled="featureDisabled"
            @click="deleteJob(scope.row)"
          >
            刪除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <pagination
      v-show="total > 0"
      :total="total"
      :page.sync="query.page"
      :limit.sync="query.pageSize"
      @pagination="fetchJobs"
    />

    <el-dialog
      :title="editingJob.id ? '編輯徵才啟事' : '新增徵才啟事'"
      :visible.sync="editorVisible"
      width="820px"
      :close-on-click-modal="false"
    >
      <el-form ref="jobForm" :model="editingJob" :rules="rules" label-width="110px">
        <el-form-item
          v-if="isContentAdmin && !editingJob.id"
          label="發布單位"
          prop="targetAgencyCode"
        >
          <el-select
            v-model="editingJob.targetAgencyCode"
            filterable
            style="width: 100%"
            placeholder="請選擇旅行社"
          >
            <el-option
              v-for="agency in agencyOptions"
              :key="agency.key"
              :label="agency.val"
              :value="agency.key"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-else-if="editingJob.id" label="發布單位">
          <el-input :value="editingJob.agencyName || '由伺服器識別'" disabled />
        </el-form-item>
        <el-form-item v-else label="發布單位">
          <el-input value="依登入旅行社自動帶入" disabled />
        </el-form-item>
        <el-form-item label="標題" prop="title">
          <el-input v-model.trim="editingJob.title" maxlength="200" show-word-limit />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="身分" prop="roleType">
              <el-select v-model="editingJob.roleType" style="width: 100%">
                <el-option label="領隊" value="Leader" />
                <el-option label="導遊" value="Guide" />
                <el-option label="皆可" value="Both" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="分類" prop="category">
              <el-input v-model.trim="editingJob.category" maxlength="100" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="目的地" prop="destination">
              <el-input v-model.trim="editingJob.destination" maxlength="200" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="集合地" prop="meetingPlace">
          <el-input v-model.trim="editingJob.meetingPlace" maxlength="200" />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="開始日" prop="startDate">
              <el-date-picker
                v-model="editingJob.startDate"
                value-format="yyyy-MM-dd"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="結束日" prop="endDate">
              <el-date-picker
                v-model="editingJob.endDate"
                value-format="yyyy-MM-dd"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="截止日" prop="applicationDeadline">
              <el-date-picker
                v-model="editingJob.applicationDeadline"
                value-format="yyyy-MM-dd"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="名額" prop="quota">
              <el-input-number v-model="editingJob.quota" :min="1" :max="999" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="薪資" prop="salaryAmount">
              <el-input-number
                v-model="editingJob.salaryAmount"
                :min="0"
                :max="9999999999.99"
                :precision="2"
                :disabled="editingJob.compensationType === 'Negotiable'"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="計價方式" prop="compensationType">
              <el-select
                v-model="editingJob.compensationType"
                style="width: 100%"
                @change="handleCompensationTypeChange"
              >
                <el-option label="總額" value="Total" />
                <el-option label="日薪" value="PerDay" />
                <el-option label="面議" value="Negotiable" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="幣別" prop="currency">
          <el-select v-model="editingJob.currency" filterable style="width: 180px">
            <el-option label="TWD" value="TWD" />
            <el-option label="JPY" value="JPY" />
            <el-option label="USD" value="USD" />
          </el-select>
        </el-form-item>
        <el-form-item label="標籤">
          <el-select
            v-model="editingJob.tags"
            multiple
            filterable
            allow-create
            default-first-option
            style="width: 100%"
            placeholder="最多 10 個標籤"
          />
        </el-form-item>
        <el-form-item label="摘要" prop="summary">
          <el-input
            v-model.trim="editingJob.summary"
            type="textarea"
            :rows="3"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="工作內容" prop="description">
          <el-input
            v-model.trim="editingJob.description"
            type="textarea"
            :rows="5"
            maxlength="4000"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="應徵條件" prop="requirements">
          <el-input
            v-model.trim="editingJob.requirements"
            type="textarea"
            :rows="4"
            maxlength="4000"
            show-word-limit
          />
        </el-form-item>
        <el-row v-if="isContentAdmin" :gutter="16">
          <el-col :span="8">
            <el-form-item label="推薦">
              <el-switch v-model="editingJob.isRecommended" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="推薦排序">
              <el-input-number v-model="editingJob.recommendSort" :min="0" :max="999999" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button @click="openPreview(editingJob)">預覽</el-button>
        <el-button @click="editorVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="saving"
          :disabled="featureDisabled"
          @click="saveJob"
        >
          儲存
        </el-button>
      </span>
    </el-dialog>

    <el-dialog title="徵才預覽" :visible.sync="previewVisible" width="680px">
      <article v-if="previewJob" class="job-preview">
        <div class="preview-heading">
          <div>
            <el-tag size="small">{{ roleLabel(previewJob.roleType) }}</el-tag>
            <h2>{{ previewJob.title || '尚未填寫標題' }}</h2>
            <p>{{ previewJob.agencyName || selectedAgencyName || '發布旅行社' }}</p>
          </div>
          <strong>{{ compensationLabel(previewJob) }}</strong>
        </div>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="目的地">{{
            previewJob.destination || '未設定'
          }}</el-descriptions-item>
          <el-descriptions-item label="集合地">{{
            previewJob.meetingPlace || '未設定'
          }}</el-descriptions-item>
          <el-descriptions-item label="工作日期">{{ dateRange(previewJob) }}</el-descriptions-item>
          <el-descriptions-item label="應徵截止">{{
            previewJob.applicationDeadline || '未設定'
          }}</el-descriptions-item>
          <el-descriptions-item label="名額">{{ previewJob.quota || 1 }} 名</el-descriptions-item>
          <el-descriptions-item label="分類">{{
            previewJob.category || '未設定'
          }}</el-descriptions-item>
        </el-descriptions>
        <section>
          <h3>摘要</h3>
          <p>{{ previewJob.summary || '未填寫' }}</p>
        </section>
        <section>
          <h3>工作內容</h3>
          <p>{{ previewJob.description || '未填寫' }}</p>
        </section>
        <section>
          <h3>應徵條件</h3>
          <p>{{ previewJob.requirements || '未填寫' }}</p>
        </section>
        <div class="preview-tags">
          <el-tag v-for="tag in previewJob.tags" :key="tag" size="small" type="info">
            {{ tag }}
          </el-tag>
        </div>
      </article>
    </el-dialog>

    <el-dialog title="應徵者" :visible.sync="applicationsVisible" width="960px">
      <p v-if="selectedJob" class="dialog-caption">{{ selectedJob.title }}</p>
      <el-alert
        v-if="applicationsError"
        type="error"
        :title="applicationsError"
        :closable="false"
        show-icon
      />
      <el-table v-loading="applicationsLoading" :data="applications" empty-text="尚無應徵資料">
        <el-table-column prop="applicantName" label="姓名" min-width="120" />
        <el-table-column label="身分" width="90">
          <template slot-scope="scope">{{ roleLabel(scope.row.roleType) }}</template>
        </el-table-column>
        <el-table-column prop="appliedAt" label="應徵時間" min-width="170" />
        <el-table-column label="狀態" width="150">
          <template slot-scope="scope">
            <el-select
              :value="normalizeApplicationStatus(scope.row.status)"
              size="mini"
              :disabled="
                isApplicationUpdating(scope.row.id) ||
                ['Accepted', 'Rejected', 'Cancelled'].includes(scope.row.status) ||
                featureDisabled
              "
              :aria-label="`${scope.row.applicantName || '應徵者'}應徵狀態`"
              @change="updateApplicationStatus(scope.row, $event)"
            >
              <el-option
                v-for="option in applicationStatusOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
                :disabled="isApplicationStatusOptionDisabled(scope.row.status, option.value)"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column prop="summary" label="自我推薦" min-width="180" show-overflow-tooltip />
        <el-table-column label="操作" width="210" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="openApplicant(scope.row, 'profile')">公開簡介</el-button>
            <el-button
              type="text"
              :disabled="!canContactApplication(scope.row) || featureDisabled"
              @click="openApplicant(scope.row, 'contact')"
            >
              邀約
            </el-button>
            <el-button
              type="text"
              :disabled="!canContactApplication(scope.row) || featureDisabled"
              @click="openApplicant(scope.row, 'chat')"
            >
              聊天室
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <pagination
        v-show="applicationsTotal > 0"
        :total="applicationsTotal"
        :page.sync="applicationsQuery.page"
        :limit.sync="applicationsQuery.pageSize"
        @pagination="fetchApplications"
      />
    </el-dialog>

    <el-dialog
      :title="
        adminContactForm.contactType === 'invitation' ? '從應徵者建立邀約' : '傳送訊息給應徵者'
      "
      :visible.sync="adminContactVisible"
      width="640px"
      :close-on-click-modal="false"
    >
      <p v-if="adminContactApplication" class="dialog-caption">
        {{ adminContactApplication.applicantName }}
      </p>
      <el-alert
        v-if="adminContactError"
        type="error"
        :title="adminContactError"
        :closable="false"
        show-icon
      />
      <el-form
        ref="adminContactForm"
        v-loading="adminContactLoading"
        :model="adminContactForm"
        :rules="adminContactRules"
        label-width="100px"
      >
        <el-form-item label="旅行團" prop="travelId">
          <el-select
            v-model="adminContactForm.travelId"
            filterable
            style="width: 100%"
            placeholder="請選擇此職缺旅行社的旅行團"
          >
            <el-option
              v-for="travel in adminContactTravels"
              :key="travel.id"
              :label="travelLabel(travel)"
              :value="travel.id"
            />
          </el-select>
        </el-form-item>
        <template v-if="adminContactForm.contactType === 'invitation'">
          <el-form-item label="邀約標題" prop="title">
            <el-input v-model.trim="adminContactForm.title" maxlength="200" show-word-limit />
          </el-form-item>
          <el-form-item label="工作日期">
            <el-date-picker
              v-model="adminContactForm.workDates"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="開始日"
              end-placeholder="結束日"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item label="回覆期限" prop="expiresAt">
            <el-date-picker
              v-model="adminContactForm.expiresAt"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              style="width: 100%"
              placeholder="請選擇未來時間"
            />
          </el-form-item>
        </template>
        <el-form-item label="內容" prop="content">
          <el-input
            v-model.trim="adminContactForm.content"
            type="textarea"
            :rows="5"
            maxlength="4000"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="adminContactVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="adminContactSubmitting"
          :disabled="adminContactLoading || adminContactTravels.length === 0 || featureDisabled"
          @click="submitAdminContact"
        >
          {{ adminContactForm.contactType === 'invitation' ? '送出邀約' : '傳送訊息' }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import {
  copyAgencyJob,
  createAgencyJob,
  deleteAgencyJob,
  getAgencyJobApplications,
  getAgencyJobs,
  updateAgencyJob,
  updateAgencyJobApplicationStatus,
  updateAgencyJobStatus,
} from '@/api/tourLeaderAgency'
import {
  copyAdminJob,
  createAdminJobApplicationConversation,
  createAdminJobApplicationInvitation,
  createAdminJob,
  deleteAdminJob,
  getAdminJobApplications,
  getAdminJobApplicationTravels,
  getAdminJobs,
  updateAdminJob,
  updateAdminJobApplicationStatus,
  updateAdminJobStatus,
} from '@/api/tourLeaderAdmin'
import { getAgencyOptions } from '@/api/travel'
import { normalizePhaseThreeResponse, toPhaseThreePagedResult } from '@/utils/tourLeaderApi'
import { isTourLeaderFeatureDisabled } from '@/utils/tourLeaderPhaseSeven'
import {
  applicationStatusLabel,
  buildApplicantHunterRoute,
  createEmptyTourLeaderJob,
  jobStatusLabel,
  normalizeApplicationStatus,
  normalizeTourLeaderJob,
  toTourLeaderJobEditor,
  toTourLeaderJobPayload,
} from '@/utils/tourLeaderJob'
import {
  hasAnyRole,
  TOUR_LEADER_CONTENT_ADMIN_ROLES,
  TOUR_LEADER_MANAGEMENT_ROLES,
} from '@/utils/tourLeaderPermissions'

function defaultQuery() {
  return {
    keyword: '',
    agencyCode: '',
    roleType: '',
    category: '',
    destination: '',
    startDate: '',
    endDate: '',
    status: '',
    sort: 'latest',
    page: 1,
    pageSize: 20,
  }
}

function defaultAdminContactForm() {
  return {
    contactType: 'message',
    travelId: null,
    title: '',
    workDates: [],
    expiresAt: '',
    content: '',
  }
}

export default {
  name: 'AgencyLeaderJobs',
  components: { Pagination },
  data() {
    return {
      query: defaultQuery(),
      jobs: [],
      total: 0,
      loading: false,
      loadError: '',
      featureDisabled: false,
      jobsRequestSerial: 0,
      editorVisible: false,
      editingJob: createEmptyTourLeaderJob(),
      saving: false,
      previewVisible: false,
      previewJob: null,
      agencyOptions: [],
      updatingJobIds: {},
      applicationsVisible: false,
      applicationsLoading: false,
      applicationsError: '',
      applications: [],
      applicationsTotal: 0,
      applicationsQuery: { page: 1, pageSize: 20 },
      applicationsRequestSerial: 0,
      selectedJob: null,
      updatingApplicationIds: {},
      adminContactVisible: false,
      adminContactLoading: false,
      adminContactSubmitting: false,
      adminContactError: '',
      adminContactApplication: null,
      adminContactTravels: [],
      adminContactForm: defaultAdminContactForm(),
      statusOptions: [
        { label: '草稿', value: 'Draft' },
        { label: '已發布', value: 'Published' },
        { label: '已下架', value: 'Unpublished' },
        { label: '已結束', value: 'Closed' },
      ],
      applicationStatusOptions: [
        { label: '待處理', value: 'Pending' },
        { label: '已查看', value: 'Viewed' },
        { label: '聯繫中', value: 'Contacting' },
        { label: '已錄取', value: 'Accepted' },
        { label: '未錄取', value: 'Rejected' },
        { label: '已取消', value: 'Cancelled' },
      ],
      rules: {
        targetAgencyCode: [{ required: true, message: '請選擇發布旅行社', trigger: 'change' }],
        title: [{ required: true, message: '請輸入標題', trigger: 'blur' }],
        roleType: [{ required: true, message: '請選擇身分', trigger: 'change' }],
        category: [{ required: true, message: '請輸入分類', trigger: 'blur' }],
        destination: [{ required: true, message: '請輸入目的地', trigger: 'blur' }],
        meetingPlace: [{ required: true, message: '請輸入集合地', trigger: 'blur' }],
        startDate: [{ required: true, message: '請選擇開始日', trigger: 'change' }],
        endDate: [{ required: true, message: '請選擇結束日', trigger: 'change' }],
        applicationDeadline: [{ required: true, message: '請選擇應徵截止日', trigger: 'change' }],
        quota: [{ required: true, message: '請輸入名額', trigger: 'change' }],
        compensationType: [{ required: true, message: '請選擇計價方式', trigger: 'change' }],
        currency: [{ required: true, message: '請選擇幣別', trigger: 'change' }],
        summary: [{ required: true, message: '請輸入摘要', trigger: 'blur' }],
        description: [{ required: true, message: '請輸入工作內容', trigger: 'blur' }],
      },
      adminContactRules: {
        travelId: [{ required: true, message: '請選擇旅行團', trigger: 'change' }],
        title: [{ required: true, message: '請輸入邀約標題', trigger: 'blur' }],
        expiresAt: [{ required: true, message: '請選擇回覆期限', trigger: 'change' }],
        content: [{ required: true, message: '請輸入內容', trigger: 'blur' }],
      },
    }
  },
  computed: {
    isContentAdmin() {
      return hasAnyRole(this.$store.getters.roles, TOUR_LEADER_CONTENT_ADMIN_ROLES)
    },
    selectedAgencyName() {
      const code = this.editingJob && this.editingJob.targetAgencyCode
      const agency = this.agencyOptions.find(option => option.key === code)
      return agency ? agency.val : ''
    },
  },
  created() {
    if (!hasAnyRole(this.$store.getters.roles, TOUR_LEADER_MANAGEMENT_ROLES)) {
      this.$router.replace('/401')
      return
    }
    this.fetchJobs()
    if (this.isContentAdmin) this.fetchAgencyOptions()
  },
  beforeDestroy() {
    this.jobsRequestSerial += 1
    this.applicationsRequestSerial += 1
  },
  methods: {
    normalizeApplicationStatus,
    jobStatusLabel,
    applicationStatusLabel,
    backToHunter() {
      // Guard redirect (e.g. permission) rejects the named push; fall back to
      // the absolute path so the button never appears dead.
      this.$router
        .push({ name: 'AgencyLeaderHunter' })
        .catch(() => this.$router.push('/agency/agencyLeaderHunter/index').catch(() => {}))
    },
    async fetchJobs() {
      const requestSerial = ++this.jobsRequestSerial
      this.loading = true
      this.loadError = ''
      try {
        const response = this.isContentAdmin
          ? await getAdminJobs(this.query)
          : await getAgencyJobs(this.query)
        const result = toPhaseThreePagedResult(response)
        if (requestSerial !== this.jobsRequestSerial) return
        this.jobs = result.items.map(normalizeTourLeaderJob)
        this.total = result.total
        this.featureDisabled = false
      } catch (error) {
        if (requestSerial !== this.jobsRequestSerial) return
        this.jobs = []
        this.total = 0
        if (!this.handleFeatureDisabled(error)) {
          this.loadError = '無法載入徵才啟事，請稍後再試。'
        }
      } finally {
        if (requestSerial === this.jobsRequestSerial) this.loading = false
      }
    },
    search() {
      this.query.page = 1
      this.fetchJobs()
    },
    handleFeatureDisabled(error) {
      if (!isTourLeaderFeatureDisabled(error)) return false
      this.jobsRequestSerial += 1
      this.applicationsRequestSerial += 1
      this.jobs = []
      this.total = 0
      this.applications = []
      this.applicationsTotal = 0
      this.loadError = ''
      this.loading = false
      this.editorVisible = false
      this.saving = false
      this.featureDisabled = true
      return true
    },
    reset() {
      this.query = defaultQuery()
      this.fetchJobs()
    },
    openEditor(job) {
      if (this.featureDisabled) return false
      this.editingJob = job ? toTourLeaderJobEditor(job) : createEmptyTourLeaderJob()
      this.editorVisible = true
      this.$nextTick(() => this.$refs.jobForm && this.$refs.jobForm.clearValidate())
    },
    openPreview(job) {
      this.previewJob = normalizeTourLeaderJob(job)
      this.previewVisible = true
    },
    validateBusinessDates() {
      const start = Date.parse(this.editingJob.startDate)
      const end = Date.parse(this.editingJob.endDate)
      const deadline = Date.parse(this.editingJob.applicationDeadline)
      if (
        !Number.isFinite(start) ||
        !Number.isFinite(end) ||
        start > end ||
        end - start > 366 * 24 * 60 * 60 * 1000
      ) {
        this.$message.error('工作結束日不可早於開始日。')
        return false
      }
      if (!Number.isFinite(deadline) || deadline > start) {
        this.$message.error('應徵截止日不可晚於工作開始日。')
        return false
      }
      if (Array.isArray(this.editingJob.tags) && this.editingJob.tags.length > 10) {
        this.$message.error('標籤最多 10 個。')
        return false
      }
      if (
        this.editingJob.compensationType !== 'Negotiable' &&
        (this.editingJob.salaryAmount === null ||
          this.editingJob.salaryAmount === '' ||
          !Number.isFinite(Number(this.editingJob.salaryAmount)) ||
          Number(this.editingJob.salaryAmount) < 0)
      ) {
        this.$message.error('請輸入正確的薪資金額。')
        return false
      }
      return true
    },
    handleCompensationTypeChange(value) {
      if (value === 'Negotiable') {
        this.editingJob.salaryAmount = null
      } else if (this.editingJob.salaryAmount === null) {
        this.editingJob.salaryAmount = 0
      }
    },
    saveJob() {
      if (this.featureDisabled) return false
      this.$refs.jobForm.validate(async valid => {
        if (!valid || !this.validateBusinessDates()) return

        this.saving = true
        const isCreate = !this.editingJob.id
        try {
          const payload = toTourLeaderJobPayload(this.editingJob, {
            includeAdminFields: this.isContentAdmin,
            includeTargetAgency: this.isContentAdmin && isCreate,
          })
          const response = this.isContentAdmin
            ? isCreate
              ? await createAdminJob(payload)
              : await updateAdminJob(this.editingJob.id, payload)
            : isCreate
              ? await createAgencyJob(payload)
              : await updateAgencyJob(this.editingJob.id, payload)
          normalizePhaseThreeResponse(response)
          this.$message.success('徵才啟事已儲存')
          this.editorVisible = false
          await this.fetchJobs()
        } catch (error) {
          this.$message.error('儲存失敗，請確認欄位與資料範圍。')
        } finally {
          this.saving = false
        }
      })
    },
    async fetchAgencyOptions() {
      try {
        const response = await getAgencyOptions()
        this.agencyOptions = Array.isArray(response.data)
          ? response.data.filter(option => option && option.key && option.val)
          : []
      } catch (error) {
        this.agencyOptions = []
        this.$message.error('無法取得可管理的旅行社清單。')
      }
    },
    async copyJob(job) {
      if (this.featureDisabled) return false
      try {
        await this.$confirm(`確定要複製「${job.title}」？複製後為草稿。`, '複製徵才', {
          type: 'info',
        })
        const response = this.isContentAdmin
          ? await copyAdminJob(job.id)
          : await copyAgencyJob(job.id)
        normalizePhaseThreeResponse(response)
        this.$message.success('已複製為草稿')
        await this.fetchJobs()
      } catch (error) {
        if (!this.isDialogCancellation(error)) this.$message.error('複製失敗。')
      }
    },
    async deleteJob(job) {
      if (this.featureDisabled) return false
      try {
        await this.$confirm(`刪除「${job.title}」後無法復原，確定繼續？`, '刪除徵才', {
          type: 'warning',
          confirmButtonText: '刪除',
        })
        const response = this.isContentAdmin
          ? await deleteAdminJob(job.id)
          : await deleteAgencyJob(job.id)
        normalizePhaseThreeResponse(response)
        this.$message.success('徵才啟事已刪除')
        if (this.jobs.length === 1 && this.query.page > 1) this.query.page -= 1
        await this.fetchJobs()
      } catch (error) {
        if (!this.isDialogCancellation(error)) this.$message.error('刪除失敗。')
      }
    },
    async changeStatus(job, status) {
      if (this.featureDisabled) return false
      try {
        await this.$confirm(`確定要將「${job.title}」設為${jobStatusLabel(status)}？`, '確認操作', {
          type: 'warning',
        })
        const response = this.isContentAdmin
          ? await updateAdminJobStatus(job.id, status)
          : await updateAgencyJobStatus(job.id, status)
        normalizePhaseThreeResponse(response)
        this.$message.success('狀態已更新')
        await this.fetchJobs()
      } catch (error) {
        if (!this.isDialogCancellation(error)) this.$message.error('狀態更新失敗。')
      }
    },
    async updateRecommendation(job, isRecommended) {
      await this.updateAdminPresentation(job, { isRecommended })
    },
    async updateRecommendSort(job, recommendSort) {
      if (recommendSort === undefined) return
      await this.updateAdminPresentation(job, { recommendSort })
    },
    async updateAdminPresentation(job, change) {
      if (this.featureDisabled) return false
      if (!this.isContentAdmin || this.isRowUpdating(job.id)) return
      this.$set(this.updatingJobIds, String(job.id), true)
      try {
        const edited = Object.assign(toTourLeaderJobEditor(job), change)
        const response = await updateAdminJob(
          job.id,
          toTourLeaderJobPayload(edited, { includeAdminFields: true })
        )
        normalizePhaseThreeResponse(response)
        Object.keys(change).forEach(key => this.$set(job, key, change[key]))
        // A list request that started before this mutation must not restore
        // stale recommendation values after the successful PUT.
        this.jobsRequestSerial += 1
        this.$message.success('推薦設定已更新')
        await this.fetchJobs()
      } catch (error) {
        this.$message.error('推薦設定更新失敗。')
      } finally {
        this.$delete(this.updatingJobIds, String(job.id))
      }
    },
    isRowUpdating(jobId) {
      return this.updatingJobIds[String(jobId)] === true
    },
    async openApplications(job) {
      this.selectedJob = job
      this.applicationsVisible = true
      this.applicationsQuery = { page: 1, pageSize: 20 }
      await this.fetchApplications()
    },
    async fetchApplications() {
      if (!this.selectedJob) return
      const requestSerial = ++this.applicationsRequestSerial
      this.applicationsLoading = true
      this.applicationsError = ''
      try {
        const response = this.isContentAdmin
          ? await getAdminJobApplications(this.selectedJob.id, this.applicationsQuery)
          : await getAgencyJobApplications(this.selectedJob.id, this.applicationsQuery)
        const result = toPhaseThreePagedResult(response)
        if (requestSerial !== this.applicationsRequestSerial) return
        this.applications = result.items.map(item => ({
          ...item,
          id: item.id === undefined ? item.applicationId : item.id,
          applicantName: item.applicantName || item.memberName || '未提供',
          roleType: item.roleType || item.memberRoleType || item.role,
          summary: item.summary || item.coverLetter || '',
          status: normalizeApplicationStatus(item.status),
        }))
        this.applicationsTotal = result.total
      } catch (error) {
        if (requestSerial !== this.applicationsRequestSerial) return
        this.applications = []
        this.applicationsTotal = 0
        this.applicationsError = '無法載入應徵資料。'
      } finally {
        if (requestSerial === this.applicationsRequestSerial) {
          this.applicationsLoading = false
        }
      }
    },
    async updateApplicationStatus(application, status) {
      if (this.featureDisabled) return false
      if (!this.selectedJob || this.isApplicationUpdating(application.id)) return
      if (normalizeApplicationStatus(application.status) === normalizeApplicationStatus(status)) {
        return
      }
      this.$set(this.updatingApplicationIds, String(application.id), true)
      try {
        const response = this.isContentAdmin
          ? await updateAdminJobApplicationStatus(this.selectedJob.id, application.id, status)
          : await updateAgencyJobApplicationStatus(this.selectedJob.id, application.id, status)
        normalizePhaseThreeResponse(response)
        this.$set(application, 'status', normalizeApplicationStatus(status))
        this.$message.success('應徵狀態已更新')
      } catch (error) {
        this.$message.error('應徵狀態更新失敗。')
      } finally {
        this.$delete(this.updatingApplicationIds, String(application.id))
      }
    },
    isApplicationUpdating(applicationId) {
      return this.updatingApplicationIds[String(applicationId)] === true
    },
    isApplicationStatusOptionDisabled(currentStatus, targetStatus) {
      if (currentStatus === targetStatus) return false
      const allowed = {
        Pending: ['Viewed', 'Contacting', 'Accepted', 'Rejected'],
        Viewed: ['Contacting', 'Accepted', 'Rejected'],
        Contacting: ['Accepted', 'Rejected'],
      }
      return !(allowed[currentStatus] || []).includes(targetStatus)
    },
    canContactApplication(application) {
      const status = normalizeApplicationStatus(application && application.status)
      return status !== 'Rejected' && status !== 'Cancelled' && status !== 'Unknown'
    },
    async openApplicant(application, action) {
      if (action !== 'profile' && this.featureDisabled) return false
      if (action !== 'profile' && !this.canContactApplication(application)) return
      if (this.isContentAdmin && action !== 'profile') {
        await this.openAdminContact(application, action)
        return
      }
      const target = buildApplicantHunterRoute(application, action)
      if (!target) {
        this.$message.error('應徵者資料不完整。')
        return
      }
      this.$router.push(target)
    },
    async openAdminContact(application, action) {
      if (this.featureDisabled) return false
      if (!this.selectedJob || !application || !application.id) {
        this.$message.error('應徵資料不完整。')
        return
      }
      this.adminContactApplication = application
      this.adminContactTravels = []
      this.adminContactError = ''
      this.adminContactForm = {
        ...defaultAdminContactForm(),
        contactType: action === 'contact' ? 'invitation' : 'message',
        workDates: [this.selectedJob.startDate, this.selectedJob.endDate].filter(Boolean),
      }
      this.adminContactVisible = true
      this.adminContactLoading = true
      this.$nextTick(() => {
        const form = this.$refs.adminContactForm
        if (form && typeof form.clearValidate === 'function') form.clearValidate()
      })
      try {
        const response = await getAdminJobApplicationTravels(this.selectedJob.id, application.id)
        const result = toPhaseThreePagedResult(response)
        this.adminContactTravels = result.items.filter(
          travel => travel && Number.isInteger(Number(travel.id)) && Number(travel.id) > 0
        )
      } catch (error) {
        this.adminContactError = '無法取得這筆應徵所屬旅行社的旅行團。'
      } finally {
        this.adminContactLoading = false
      }
    },
    submitAdminContact() {
      if (this.featureDisabled) return false
      this.$refs.adminContactForm.validate(async valid => {
        if (!valid || !this.selectedJob || !this.adminContactApplication) return
        const isInvitation = this.adminContactForm.contactType === 'invitation'
        if (isInvitation && !this.isFutureDateTime(this.adminContactForm.expiresAt)) {
          this.$message.error('回覆期限必須晚於現在。')
          return
        }

        this.adminContactSubmitting = true
        try {
          const jobId = this.selectedJob.id
          const applicationId = this.adminContactApplication.id
          const basePayload = {
            travelId: Number(this.adminContactForm.travelId),
            content: this.adminContactForm.content,
            clientMessageId: this.createClientMessageId(),
          }
          const response = isInvitation
            ? await createAdminJobApplicationInvitation(jobId, applicationId, {
                ...basePayload,
                title: this.adminContactForm.title,
                workStartAt: this.adminContactForm.workDates[0]
                  ? this.toTaipeiDateTime(this.adminContactForm.workDates[0])
                  : undefined,
                workEndAt: this.adminContactForm.workDates[1]
                  ? this.toTaipeiDateTime(this.adminContactForm.workDates[1], true)
                  : undefined,
                expiresAt: this.toTaipeiDateTime(this.adminContactForm.expiresAt),
              })
            : await createAdminJobApplicationConversation(jobId, applicationId, {
                ...basePayload,
                messageType: 'text',
              })
          normalizePhaseThreeResponse(response)
          this.$message.success(isInvitation ? '邀約已送出' : '訊息已送出')
          this.adminContactVisible = false
        } catch (error) {
          this.$message.error('未能完成聯繫，請確認旅行團與應徵資格。')
        } finally {
          this.adminContactSubmitting = false
        }
      })
    },
    isFutureDateTime(value) {
      if (!value) return false
      const parsed = Date.parse(this.toTaipeiDateTime(value))
      return Number.isFinite(parsed) && parsed > Date.now()
    },
    toTaipeiDateTime(value, endOfDay = false) {
      if (typeof value !== 'string' || !value.trim()) throw new Error('invalid_datetime')
      const normalized = value.trim().replace(' ', 'T')
      const withTime = /^\d{4}-\d{2}-\d{2}$/.test(normalized)
        ? `${normalized}T${endOfDay ? '23:59:59' : '00:00:00'}`
        : normalized
      return /(?:Z|[+-]\d{2}:\d{2})$/.test(withTime) ? withTime : `${withTime}+08:00`
    },
    createClientMessageId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID()
      }
      if (!window.crypto || typeof window.crypto.getRandomValues !== 'function') {
        throw new Error('secure_random_unavailable')
      }
      const bytes = new Uint8Array(16)
      window.crypto.getRandomValues(bytes)
      bytes[6] = (bytes[6] & 0x0f) | 0x40
      bytes[8] = (bytes[8] & 0x3f) | 0x80
      const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
        16,
        20
      )}-${hex.slice(20)}`
    },
    travelLabel(travel) {
      return [travel.travelName, travel.tourGroupNo, travel.beginDate].filter(Boolean).join(' / ')
    },
    isDialogCancellation(error) {
      return error === 'cancel' || error === 'close'
    },
    statusTagType(status) {
      return (
        { Draft: 'info', Published: 'success', Unpublished: 'warning', Closed: 'danger' }[status] ||
        'info'
      )
    },
    roleLabel(roleType) {
      const normalized = String(roleType || '').toLowerCase()
      return { leader: '領隊', guide: '導遊', both: '皆可' }[normalized] || '未指定'
    },
    dateRange(job) {
      return [job.startDate, job.endDate].filter(Boolean).join(' 至 ') || '未設定'
    },
    compensationLabel(job) {
      if (!job) return '未設定'
      if (String(job.compensationType).toLowerCase() === 'negotiable') return '面議'
      const amount = Number(job.salaryAmount)
      if (!Number.isFinite(amount)) return '未設定'
      const suffix =
        {
          perday: '／日',
          total: '（總額）',
        }[String(job.compensationType || '').toLowerCase()] || ''
      return `${job.currency || 'TWD'} ${amount.toLocaleString('zh-TW')}${suffix}`
    },
  },
}
</script>

<style lang="scss" scoped>
.leader-jobs-page {
  .page-heading,
  .page-title,
  .preview-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .page-heading {
    gap: 16px;
    margin-bottom: 12px;

    h2 {
      margin: 0;
      font-size: 20px;
      line-height: 1.4;
    }

    p {
      margin: 0;
      color: #606266;
    }
  }

  .page-title {
    justify-content: flex-start;
    gap: 16px;
  }

  .filter-card,
  .load-alert {
    margin-bottom: 16px;
  }

  .dialog-caption {
    margin-top: 0;
    color: #606266;
  }

  .job-preview {
    color: #303133;

    .preview-heading {
      align-items: flex-start;
      margin-bottom: 20px;

      h2 {
        margin: 10px 0 4px;
      }

      p {
        margin: 0;
        color: #606266;
      }

      strong {
        color: #b76e00;
        font-size: 18px;
      }
    }

    section {
      margin-top: 20px;

      h3 {
        margin-bottom: 8px;
      }

      p {
        margin: 0;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
    }

    .preview-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 20px;
    }
  }
}

@media (max-width: 768px) {
  .leader-jobs-page {
    .page-heading,
    .page-title {
      align-items: flex-start;
      flex-direction: column;
    }
  }
}

// Element dialogs keep this page's scope on their wrapper, including when
// append-to-body moves the chat wrapper outside the page container.
@media (max-width: 768px) {
  ::v-deep .el-dialog {
    display: flex;
    flex-direction: column;
    max-width: calc(100vw - 24px);
    max-height: 92vh;
    margin-top: 4vh !important;
    margin-bottom: 4vh;
  }

  ::v-deep .el-dialog__header,
  ::v-deep .el-dialog__footer {
    flex-shrink: 0;
  }

  ::v-deep .el-dialog__body {
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    overflow-wrap: anywhere;
  }

  ::v-deep .el-dialog__footer {
    padding: 12px 16px;
  }

  ::v-deep .el-dialog__footer > span,
  ::v-deep .el-dialog__footer > div {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }

  ::v-deep .el-dialog__footer .el-button + .el-button {
    margin-left: 0;
  }

  ::v-deep .el-dialog .el-form-item__label {
    display: block;
    float: none;
    text-align: left;
    line-height: 28px;
  }

  ::v-deep .el-dialog .el-form-item__content {
    margin-left: 0 !important;
  }

  ::v-deep .el-dialog .el-col {
    width: 100%;
  }

  .job-preview .preview-heading {
    flex-wrap: wrap;
    gap: 12px;
  }
}
</style>
